/**
 * Custom Forms - Client-side Validation
 * Reusable form validation for contact and returns forms
 */

class CustomFormValidator {
  constructor(form) {
    this.form = form;
    this.init();
  }

  init() {
    // Add real-time validation
    const inputs = this.form.querySelectorAll('.field__input');
    inputs.forEach(input => {
      input.addEventListener('blur', () => this.validateField(input));
      input.addEventListener('input', () => this.clearFieldError(input));
    });

    // Add submit validation
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Handle select field label animation
    const selects = this.form.querySelectorAll('select.field__input');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        if (select.value !== '') {
          select.setAttribute('value', select.value);
        } else {
          select.removeAttribute('value');
        }
      });
    });
  }

  validateField(field) {
    const fieldWrapper = field.closest('.field');
    let isValid = true;
    let errorMessage = '';

    // Check if field is required and empty
    if (field.hasAttribute('required') && !field.value.trim()) {
      isValid = false;
      errorMessage = 'This field is required';
    }

    // Email validation
    if (field.type === 'email' && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address';
      }
    }

    // Phone validation (optional but if filled, should be valid)
    if (field.type === 'tel' && field.value.trim()) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Please enter a valid phone number';
      }
    }

    if (!isValid) {
      this.showFieldError(fieldWrapper, errorMessage);
    } else {
      this.clearFieldError(field);
    }

    return isValid;
  }

  showFieldError(fieldWrapper, message) {
    const input = fieldWrapper.querySelector('.field__input');

    // Add error class
    fieldWrapper.classList.add('field--with-error');
    input.setAttribute('aria-invalid', 'true');

    // Check if error message already exists
    let errorElement = fieldWrapper.querySelector('.form__message--error');

    if (!errorElement) {
      errorElement = document.createElement('small');
      errorElement.className = 'form__message form__message--error';
      errorElement.setAttribute('role', 'alert');
      fieldWrapper.appendChild(errorElement);
    }

    errorElement.textContent = message;
  }

  clearFieldError(field) {
    const fieldWrapper = field.closest('.field');
    const errorElement = fieldWrapper.querySelector('.form__message--error');

    if (errorElement && field.value.trim()) {
      fieldWrapper.classList.remove('field--with-error');
      field.removeAttribute('aria-invalid');

      // Don't remove the error element if it's for email (managed by Shopify)
      if (!field.id.includes('email')) {
        errorElement.remove();
      }
    }
  }

  handleSubmit(event) {
    let isFormValid = true;
    const inputs = this.form.querySelectorAll('.field__input[required]');

    inputs.forEach(input => {
      if (!this.validateField(input)) {
        isFormValid = false;
      }
    });

    if (!isFormValid) {
      event.preventDefault();

      // Scroll to first error
      const firstError = this.form.querySelector('.field--with-error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.querySelector('.field__input').focus();
      }

      // Show general error message
      this.showFormError('Please correct the errors above before submitting.');
    }
  }

  showFormError(message) {
    // Check if error message already exists
    let errorBlock = this.form.querySelector('.form-status--error');

    if (!errorBlock) {
      errorBlock = document.createElement('div');
      errorBlock.className = 'form-status form-status--error';
      errorBlock.innerHTML = `
        <svg class="icon icon-error" aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 13 13">
          <path d="M6.5 12.35C2.91 12.35.65 10.09.65 6.5S2.91.65 6.5.65s5.85 2.26 5.85 5.85-2.26 5.85-5.85 5.85zm0-1.08c2.63 0 4.77-2.14 4.77-4.77S9.13 1.73 6.5 1.73 1.73 3.87 1.73 6.5s2.14 4.77 4.77 4.77zM5.42 7.08L4.27 5.93a.54.54 0 1 1 .77-.77l1.15 1.15L7.34 5.16a.54.54 0 1 1 .77.77L6.96 7.08l1.15 1.15a.54.54 0 1 1-.77.77L6.19 7.85 5.04 9a.54.54 0 0 1-.77-.77l1.15-1.15z" fill="currentColor"/>
        </svg>
        <span>${message}</span>
      `;

      this.form.insertBefore(errorBlock, this.form.firstChild);
    } else {
      errorBlock.querySelector('span').textContent = message;
    }
  }
}

// Initialize forms when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const contactForm = document.getElementById('CustomContactForm');
  const returnsForm = document.getElementById('CustomReturnsForm');

  if (contactForm) {
    new CustomFormValidator(contactForm);
  }

  if (returnsForm) {
    new CustomFormValidator(returnsForm);
  }
});
